// Invite acceptance flow. Reached via /accept-invite?orgId=X&inviteId=Y.
// Reachable by signed-in users only; if signed out, RequireAuth bounces
// to /signin first and the user comes back here after.

import { useEffect, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, Check, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { acceptInvite, getInvite, getOrg } from '../lib/orgs';
import type { Org, PendingInvite } from '../lib/types';
import SignOutButton from '../components/auth/SignOutButton';
import {
  AuthShell,
  Badge,
  Button,
  Row,
  Spinner,
  Stack,
} from '../components/ui';
import { ErrorBanner } from './SigninPage';

export default function AcceptInvitePage() {
  const [params] = useSearchParams();
  const orgId = params.get('orgId') ?? '';
  const inviteId = params.get('inviteId') ?? '';
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [invite, setInvite] = useState<PendingInvite | null>(null);
  const [org, setOrg] = useState<Org | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) return;
    if (!orgId || !inviteId) {
      setLoadError('Invite link is malformed.');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const i = await getInvite(orgId, inviteId);
        if (cancelled) return;
        if (!i) {
          setLoadError(
            'Invite not found. It may have been cancelled, accepted, or sent to a different email.',
          );
          return;
        }
        setInvite(i);
        try {
          const o = await getOrg(orgId);
          if (!cancelled) setOrg(o);
        } catch (orgErr) {
          if (!cancelled) {
            console.debug('[invite] could not read org (expected pre-acceptance)', orgErr);
          }
        }
      } catch (err) {
        console.error('[invite] load failed', err);
        if (!cancelled) {
          setLoadError(
            'Could not load this invite. It may have been sent to a different email — sign in to that account and try again.',
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authLoading, user, orgId, inviteId]);

  if (authLoading) return null;
  if (!user) {
    return (
      <Navigate
        to={`/signin?next=${encodeURIComponent(`/accept-invite?orgId=${orgId}&inviteId=${inviteId}`)}`}
        replace
      />
    );
  }

  if (!orgId || !inviteId) {
    return (
      <AuthShell>
        <ErrorBanner message="Invite link is malformed." />
      </AuthShell>
    );
  }

  const emailMatches =
    invite && user.email && invite.email.toLowerCase() === user.email.toLowerCase();

  async function accept() {
    if (busy) return;
    setBusy(true);
    setActionError(null);
    try {
      await acceptInvite(orgId, inviteId);
      navigate('/', { replace: true });
    } catch (err) {
      console.error('[invite] accept failed', err);
      setActionError(extractMessage(err));
      setBusy(false);
    }
  }

  return (
    <AuthShell>
      <Stack gap={6}>
        <Stack gap={2}>
          <Row gap={2} align="center">
            <Mail size={20} strokeWidth={1.75} color="var(--color-accent)" />
            <h1 style={{ fontSize: 'var(--fs-2xl)' }}>Join an org</h1>
          </Row>
        </Stack>

        {loadError && <ErrorBanner message={loadError} />}

        {!loadError && !invite && (
          <Row gap={2} align="center">
            <Spinner /> <span className="ds-text-muted">Loading invite…</span>
          </Row>
        )}

        {invite && (
          <Stack gap={5}>
            <div
              style={{
                background: 'var(--color-surface-elevated)',
                border: '1px solid var(--color-border-strong)',
                borderRadius: 'var(--radius-lg)',
                padding: 'var(--space-5)',
              }}
            >
              <Stack gap={3}>
                <p className="ds-text-muted ds-text-sm">You&apos;ve been invited to join</p>
                <h2 style={{ fontSize: 'var(--fs-xl)' }}>{org?.name ?? 'an org'}</h2>
                <Row gap={2} align="center">
                  <span className="ds-text-muted ds-text-sm">As</span>
                  <Badge tone={invite.role === 'admin' ? 'accent' : 'neutral'} dot>
                    {invite.role}
                  </Badge>
                </Row>
              </Stack>
            </div>

            {!emailMatches && (
              <ErrorBanner
                message={`This invite was sent to ${invite.email}, but you're signed in as ${user.email}. Sign out and sign in with the right account.`}
              />
            )}

            {actionError && <ErrorBanner message={actionError} />}

            <Row gap={2}>
              <Button
                variant="primary"
                onClick={accept}
                disabled={busy || !emailMatches}
                loading={busy}
                iconLeft={!busy ? <Check size={14} strokeWidth={2} /> : undefined}
              >
                Accept invite
              </Button>
              <Button
                variant="ghost"
                onClick={() => navigate('/', { replace: true })}
                disabled={busy}
                iconLeft={<X size={14} strokeWidth={2} />}
              >
                Decline
              </Button>
            </Row>

            {!emailMatches && (
              <Row justify="center" gap={2}>
                <span className="ds-text-tertiary ds-text-xs">Wrong account?</span>
                <SignOutButton />
              </Row>
            )}
          </Stack>
        )}
      </Stack>
    </AuthShell>
  );
}

function extractMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'message' in error) {
    const m = (error as { message: unknown }).message;
    if (typeof m === 'string' && m.length > 0) return m;
  }
  return 'Something went wrong. Try again.';
}
